/**
 * UNE MESURE, UN SEUIL, UN INSTANT, UNE DURÉE : LA SEULE MISE EN FORME DU MODULE.
 * ============================================================================
 *
 * Règle : une absence n'est jamais un 0. `null`, `undefined` ou un nombre
 * illisible rendent « — » (une mesure) ou `null` (un seuil, un instant, une
 * durée), et l'appelant dit l'absence à la place de la donnée. L'unité vient
 * de l'alarme (`seuils_figes.unite`), jamais d'une constante ; une unité que
 * l'écran ne connaît pas n'est pas affichée plutôt que devinée.
 *
 * Les nombres suivent la maquette (`nb`, S:2786-2791) : une décimale pour une
 * température, aucune sinon, virgule décimale et vrai signe moins.
 */
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type { AlarmesV2SeuilsFiges } from "@modules/alarmes-v2/types/alarmes-v2"

/** Le fuseau des écrans : celui des sites suivis. */
const FUSEAU = "Europe/Paris"

/** Vrai pour un nombre fini. */
const estNombre = (v: unknown): v is number =>
    typeof v === "number" && Number.isFinite(v)

/** Le symbole d'une unité servie, ou `null` si l'écran ne la connaît pas. */
function symbole(unite: string | null | undefined): string | null {
    if (!unite) return null
    const unites = MESSAGES_ALARMES_V2.unites as Record<string, string>
    return unites[unite] ?? null
}

/** Un nombre à la française, au nombre de décimales de son unité. */
function nombre(v: number, unite: string | null | undefined): string {
    const decimales = unite === "degre_celsius" ? 1 : 0
    return v.toFixed(decimales).replace(".", ",").replace("-", "−")
}

/**
 * Une valeur et son unité ; « — » quand la valeur est absente.
 * @param v - la valeur servie
 * @param unite - le code d'unité servi avec elle
 * @param t - la traduction
 */
export function formaterMesure(
    v: number | null | undefined,
    unite: string | null | undefined,
    t: Traduire
): string {
    if (!estNombre(v)) return t("alarmesV2.mesure.absente")
    const s = symbole(unite)
    return s ? `${nombre(v, unite)} ${s}` : nombre(v, unite)
}

/**
 * Le seuil figé d'une alarme, ou `null` quand aucune borne n'est servie.
 * @param seuils - `seuils_figes` de l'alarme
 * @param t - la traduction
 */
export function formaterSeuil(
    seuils: AlarmesV2SeuilsFiges | null | undefined,
    t: Traduire
): string | null {
    if (!seuils) return null
    const { borne_basse: bas, borne_haute: haut, unite } = seuils
    if (estNombre(bas) && estNombre(haut)) {
        return t("alarmesV2.mesure.entre", {
            bas: formaterMesure(bas, unite, t),
            haut: formaterMesure(haut, unite, t),
        })
    }
    if (estNombre(haut)) return formaterMesure(haut, unite, t)
    if (estNombre(bas)) return formaterMesure(bas, unite, t)
    return null
}

/** Une date ISO lisible, ou `null`. */
function lire(iso: string | null | undefined): Date | null {
    if (typeof iso !== "string" || iso === "") return null
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? null : d
}

/** Les parties d'une date à l'heure de Paris. */
function parties(d: Date): Record<string, string> {
    const format = new Intl.DateTimeFormat("fr-FR", {
        timeZone: FUSEAU,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    })
    return Object.fromEntries(
        format.formatToParts(d).map((p) => [p.type, p.value])
    )
}

/**
 * Un instant : « le 02/10 à 02h03 », à l'heure de Paris ; `null` s'il manque.
 * @param iso - l'instant servi
 * @param t - la traduction
 */
export function formaterInstant(
    iso: string | null | undefined,
    t: Traduire
): string | null {
    const d = lire(iso)
    if (!d) return null
    const p = parties(d)
    return t("alarmesV2.details.instant", {
        jour: `${p.day}/${p.month}`,
        heure: `${p.hour}h${p.minute}`,
    })
}

/**
 * Une date : « 18/09/2026 », à l'heure de Paris ; `null` si elle manque.
 * @param iso - la date servie
 */
export function formaterDate(iso: string | null | undefined): string | null {
    const d = lire(iso)
    if (!d) return null
    const p = parties(d)
    return `${p.day}/${p.month}/${p.year}`
}

/**
 * Une durée en mots (`duree`, S:2892-2899) ; `null` si elle manque.
 * @param secondes - la durée servie
 * @param t - la traduction
 */
export function formaterDuree(
    secondes: number | null | undefined,
    t: Traduire
): string | null {
    if (!estNombre(secondes) || secondes < 0) return null
    if (secondes >= 172800)
        return t("alarmesV2.durees.jours", { n: Math.round(secondes / 86400) })
    const minutes = Math.round(secondes / 60)
    if (minutes < 60) return t("alarmesV2.durees.minutes", { n: minutes })
    const h = Math.floor(minutes / 60)
    const reste = minutes % 60
    if (reste === 0)
        return h > 1
            ? t("alarmesV2.durees.heures", { n: h })
            : t("alarmesV2.durees.heure")
    return t("alarmesV2.durees.heuresMinutes", {
        h,
        mm: String(reste).padStart(2, "0"),
    })
}
